import { useSiteContent } from '@/hooks/useSiteContent';
import ContentState from '@/components/feature/ContentState';

interface WrittenCommitmentsData {
  title: string;
  highlight: string;
  subtitle: string;
  categories: { name: string; items: { label: string; committed: boolean }[] }[];
  banner: { title: string; subtitle: string; cta: string };
}

export default function WrittenCommitments() {
  const { data, loading, error, refresh } = useSiteContent<WrittenCommitmentsData>(
    'why_us_written_commitments',
  );

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {data.title}{' '}
            <span className="text-primary-500">{data.highlight}</span>
          </h2>
          <p className="text-center text-foreground-600 max-w-2xl mx-auto mb-12 md:mb-16 text-sm md:text-base">
            {data.subtitle}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {data.categories.map((cat) => (
              <div
                key={cat.name}
                className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6"
              >
                <h3 className="text-xs font-bold tracking-widest uppercase text-primary-500 mb-4 pb-3 border-b border-background-200/70">
                  {cat.name}
                </h3>
                <ul className="space-y-3">
                  {cat.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                      <span className="text-sm text-foreground-700">{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Banner */}
          <div className="bg-accent-50 border border-accent-200/70 rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-accent-500 rounded-full flex items-center justify-center shrink-0">
                <i className="ri-star-fill text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-foreground-950">
                  {data.banner.title}
                </h3>
                <p className="text-sm text-foreground-600 mt-1">
                  {data.banner.subtitle}
                </p>
              </div>
            </div>
            <button className="shrink-0 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap">
              {data.banner.cta}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}